// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const STORAGE_KEY = "rostr.google.intent";

export function saveGoogleIntent(intent, payload = null) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ intent, payload }));
}

export function startGoogleFlow(intent, payload = null) {
  saveGoogleIntent(intent, payload);
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { hydrateSession } = useAuth();
  const { t } = useI18n();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError(t("no_session"));
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : { intent: "login", payload: null };
    sessionStorage.removeItem(STORAGE_KEY);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/complete", {
          session_id: sessionId,
          intent: stored.intent || "login",
          payload: stored.payload || null,
        });
        // Fase 6.5C — the access JWT is now set as an HttpOnly cookie by
        // the backend. We must NEVER persist it in localStorage.
        // Fase 6.5B — hydrateSession fetches /legal/status BEFORE setUser,
        // so RequireAuth flips straight to LegalAcceptance if needed. No
        // flash of protected pages for a stale-Terms user.
        await hydrateSession(data.user);
        const dest = ["owner", "manager", "teamlead"].includes(data.user.role)
          ? "/dashboard"
          : "/my-roster";
        const welcome =
          stored.intent === "create_company"
            ? t("company_created_toast").replace("{name}", data.company?.name || "")
            : stored.intent === "join"
            ? t("welcome_team")
            : t("logged_in_google");
        toast.success(welcome);
        navigate(dest, { replace: true });
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
        setError(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] relative overflow-hidden grid place-items-center px-6">
      <div className="hero-mesh" aria-hidden />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-8 max-w-md w-full text-center relative z-10"
      >
        {!error ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center mb-5">
              <Loader2 size={22} className="animate-spin" />
            </div>
            <div className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))]">
              {t("google_linking")}
            </div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2">
              {t("google_wait")}
            </div>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center mb-5">
              <XCircle size={22} />
            </div>
            <div className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))]">
              {t("login_failed")}
            </div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2" data-testid="google-error">
              {error}
            </div>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="btn btn-primary mt-6"
              data-testid="google-error-back"
            >
              {t("back_to_login")}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
